import { create } from "zustand";
import { brandKitApi } from "../services/brand-kit-api";
import type {
	BrandKitFaviconResponse,
	BrandKitLogoIconOnlyResponse,
	BrandKitLogoIconWithLabelResponse,
	BrandKitLogoInvertedIconOnlyResponse,
	BrandKitLogoInvertedIconWithLabelResponse,
	BrandKitLogoInvertedLabelOnlyResponse,
	BrandKitLogoLabelOnlyResponse,
	BrandKitState,
	BrandKitUpdateRequest,
} from "../types";

export const useBrandKitStore = create<BrandKitState>((set) => ({
	brandKit: null,

	view: async (workspaceId: string) => {
		const result = await brandKitApi.view(workspaceId);

		if (result.success && result.data) {
			set({ brandKit: result.data });
			return result.data;
		}

		return null;
	},

	update: async (workspaceId: string, request: BrandKitUpdateRequest) => {
		const result = await brandKitApi.update(workspaceId, request);

		if (result.success && result.data) {
			set({ brandKit: result.data });
			return result;
		}

		return null;
	},

	updateIconWithLabel: async (workspaceId: string, file: File) => {
		const result = await brandKitApi.updateIconWithLabel(workspaceId, file);

		if (result.success && result.data && "logo_icon_with_label_url" in result.data) {
			const data = result.data as BrandKitLogoIconWithLabelResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_icon_with_label_url: data.logo_icon_with_label_url } : null,
			}));
			return data;
		}

		return null;
	},

	deleteIconWithLabel: async (workspaceId: string) => {
		const result = await brandKitApi.deleteIconWithLabel(workspaceId);

		if (result.success && result.data && "logo_icon_with_label_url" in result.data) {
			const data = result.data as BrandKitLogoIconWithLabelResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_icon_with_label_url: null } : null,
			}));
			return data;
		}

		return null;
	},

	updateLogoIconOnly: async (workspaceId: string, file: File) => {
		const result = await brandKitApi.updateLogoIconOnly(workspaceId, file);

		if (result.success && result.data && "logo_icon_only_url" in result.data) {
			const data = result.data as BrandKitLogoIconOnlyResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_icon_only_url: data.logo_icon_only_url } : null,
			}));
			return data;
		}

		return null;
	},

	deleteLogoIconOnly: async (workspaceId: string) => {
		const result = await brandKitApi.deleteLogoIconOnly(workspaceId);

		if (result.success && result.data && "logo_icon_only_url" in result.data) {
			const data = result.data as BrandKitLogoIconOnlyResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_icon_only_url: null } : null,
			}));
			return data;
		}

		return null;
	},

	updateLogoLabelOnly: async (workspaceId: string, file: File) => {
		const result = await brandKitApi.updateLogoLabelOnly(workspaceId, file);

		if (result.success && result.data && "logo_label_only_url" in result.data) {
			const data = result.data as BrandKitLogoLabelOnlyResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_label_only_url: data.logo_label_only_url } : null,
			}));
			return data;
		}

		return null;
	},

	deleteLogoLabelOnly: async (workspaceId: string) => {
		const result = await brandKitApi.deleteLogoLabelOnly(workspaceId);

		if (result.success && result.data && "logo_label_only_url" in result.data) {
			const data = result.data as BrandKitLogoLabelOnlyResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_label_only_url: null } : null,
			}));
			return data;
		}

		return null;
	},

	updateFavicon: async (workspaceId: string, file: File) => {
		const result = await brandKitApi.updateFavicon(workspaceId, file);

		if (result.success && result.data && "favicon_url" in result.data) {
			const data = result.data as BrandKitFaviconResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, favicon_url: data.favicon_url } : null,
			}));
			return data;
		}

		return null;
	},

	deleteFavicon: async (workspaceId: string) => {
		const result = await brandKitApi.deleteFavicon(workspaceId);

		if (result.success && result.data && "favicon_url" in result.data) {
			const data = result.data as BrandKitFaviconResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, favicon_url: null } : null,
			}));
			return data;
		}

		return null;
	},

	updateLogoInvertedIconOnly: async (workspaceId: string, file: File) => {
		const result = await brandKitApi.updateLogoInvertedIconOnly(workspaceId, file);

		if (result.success && result.data && "logo_inverted_icon_only_url" in result.data) {
			const data = result.data as BrandKitLogoInvertedIconOnlyResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_inverted_icon_only_url: data.logo_inverted_icon_only_url } : null,
			}));
			return data;
		}

		return null;
	},

	deleteLogoInvertedIconOnly: async (workspaceId: string) => {
		const result = await brandKitApi.deleteLogoInvertedIconOnly(workspaceId);

		if (result.success && result.data && "logo_inverted_icon_only_url" in result.data) {
			const data = result.data as BrandKitLogoInvertedIconOnlyResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_inverted_icon_only_url: null } : null,
			}));
			return data;
		}

		return null;
	},

	updateLogoInvertedIconWithLabel: async (workspaceId: string, file: File) => {
		const result = await brandKitApi.updateLogoInvertedIconWithLabel(workspaceId, file);

		if (result.success && result.data && "logo_inverted_icon_with_label_url" in result.data) {
			const data = result.data as BrandKitLogoInvertedIconWithLabelResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_inverted_icon_with_label_url: data.logo_inverted_icon_with_label_url } : null,
			}));
			return data;
		}

		return null;
	},

	deleteLogoInvertedIconWithLabel: async (workspaceId: string) => {
		const result = await brandKitApi.deleteLogoInvertedIconWithLabel(workspaceId);

		if (result.success && result.data && "logo_inverted_icon_with_label_url" in result.data) {
			const data = result.data as BrandKitLogoInvertedIconWithLabelResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_inverted_icon_with_label_url: null } : null,
			}));
			return data;
		}

		return null;
	},

	updateLogoInvertedLabelOnly: async (workspaceId: string, file: File) => {
		const result = await brandKitApi.updateLogoInvertedLabelOnly(workspaceId, file);

		if (result.success && result.data && "logo_inverted_label_only_url" in result.data) {
			const data = result.data as BrandKitLogoInvertedLabelOnlyResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_inverted_label_only_url: data.logo_inverted_label_only_url } : null,
			}));
			return data;
		}

		return null;
	},

	deleteLogoInvertedLabelOnly: async (workspaceId: string) => {
		const result = await brandKitApi.deleteLogoInvertedLabelOnly(workspaceId);

		if (result.success && result.data && "logo_inverted_label_only_url" in result.data) {
			const data = result.data as BrandKitLogoInvertedLabelOnlyResponse;
			set((state) => ({
				brandKit: state.brandKit ? { ...state.brandKit, logo_inverted_label_only_url: null } : null,
			}));
			return data;
		}

		return null;
	},
}));
